'use client'

import React, { useState, useEffect } from 'react'
import dayjs from 'dayjs'
import 'dayjs/locale/fr'
import 'dayjs/locale/en'
import { Calendar, Clock, Video, XCircle, MapPin } from 'react-feather'
import { useLocale } from '@/lib/next-shim'
import { t } from '@lingui/core/macro'

import { ConsultationType } from '@/core/graphql/types'
import { Button } from '@/components/ui/button'
import { IconText } from '@/components/ui/IconText'
import { CardActions, CardHeader } from '@/components/ui/cards/layout'
import { cn } from '@/lib/utils'
import DoctorIcon from '@/assets/svg/Doctor'

import type { Consultation } from '../model/types'
import { useConsultationActions } from '../model/useConsultationActions'
import { ConsultationStatusBadge } from './ConsultationStatusBadge'
import { ConsultationTypeBadge } from './ConsultationTypeBadge'

export type ConsultationCardProps = {
  consultation: Consultation
  onClick?: (consultation: Consultation) => void
  className?: string
}

export function ConsultationCard({ consultation, onClick, className }: ConsultationCardProps) {
  const lang = useLocale()
  const { isScheduled, hasWaitingRoom, canCancel, cancel, join } = useConsultationActions(consultation)

  const [formattedDate, setFormattedDate] = useState<string | null>(null)
  const [formattedTime, setFormattedTime] = useState<string | null>(null)
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
    setFormattedDate(dayjs(consultation.startDate).locale(lang).format('ddd D MMM'))
    setFormattedTime(dayjs(consultation.startDate).locale(lang).format('HH[h]mm'))
  }, [consultation.startDate, lang])

  return (
    <div
      data-testid="account-consultation-card"
      className={cn(
        'min-w-0 w-full sm:w-full md:w-[50vw] lg:w-[40vw] xl:w-[35vw] 2xl:w-[30vw] drop-shadow-lg bg-white gap-1 flex flex-col rounded-lg hover:border-opacity-100 hover:shadow-lg transition-all duration-300 h-full overflow-hidden',
        className
      )}
      onClick={() => onClick?.(consultation)}
    >
      <CardHeader className="p-4">
        <div className="w-5/6 overflow-hidden flex flex-col gap-1">
          <p className="truncate text-blue-900 capitalize font-bold text-md">
            {consultation.medecin ? `Dr. ${consultation.medecin.userInfo.surname} ${consultation.medecin.userInfo.name}` : t`My Consultations`}
          </p>
          <p className="text-zinc-500 text-sm md:text-md capitalize truncate">{consultation.medecin?.speciality || '-'}</p>
          <div className="flex items-center gap-2 flex-wrap">
            {consultation.type && <ConsultationTypeBadge type={consultation.type as ConsultationType} />}
            <ConsultationStatusBadge status={consultation.status} />
          </div>
        </div>
        <div className="w-1/6 pb-1 rounded-full overflow-hidden flex flex-row justify-center h-12">
          <DoctorIcon className="w-full h-full object-cover" isFemale={consultation.medecin?.userInfo.gender === 'F'} />
        </div>
      </CardHeader>

      <div className="infos h-auto py-3 grid grid-cols-4 w-full flex-grow px-4">
        <div className="col-span-4 md:col-span-2 gap-3 flex flex-col items-start justify-start">
          <IconText icon={<Calendar size={15} />} text={isClient ? formattedDate : '-'} iconClassName="text-zinc-500" />
          <IconText icon={<Clock size={15} />} text={isClient ? formattedTime : '-'} iconClassName="text-zinc-500" />
        </div>
        <div className="col-span-4 md:col-span-2 gap-2 flex flex-col items-start justify-start">
          {/* Additional Info like Waiting Room Status */}
          {hasWaitingRoom && isScheduled && (
            <IconText
              icon={<MapPin size={15} />}
              text={t`In queue`}
              className="text-orange-500 bg-orange-50 px-2 py-0.5 rounded-md"
              iconClassName="text-orange-500"
            />
          )}
        </div>
      </div>

      <CardActions>
        <div className="flex flex-row justify-center gap-2 w-full">
          {canCancel && (
            <Button
              data-testid="consultation-cancel"
              variant="ghost"
              size="icon"
              className="rounded-full hover:bg-red-50 hover:text-red-500"
              onClick={cancel}
              aria-label={t`Cancel`}
              title={t`Cancel`}
            >
              <XCircle className="h-5 w-5" aria-hidden="true" />
            </Button>
          )}

          {isScheduled ? (
            <Button
              data-testid="consultation-join"
              className="flex-1 bg-primary hover:bg-blue-700 py-2 px-4 rounded-full flex flex-row justify-center"
              onClick={join}
            >
              <Video className="h-5 w-5 text-white font-bold mr-2" aria-hidden="true" />
              <span className="text-white font-bold">{t`Join`}</span>
            </Button>
          ) : (
            /* Fallback or details link if needed */
            <div />
          )}
        </div>
      </CardActions>
    </div>
  )
}
